import { ReactNode, useContext, useMemo } from 'react'

import { theme } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Headphone, Pause, Play } from '@hhgtech/icons/other'
import { Box, Tooltip, TooltipProps } from '@mantine/core'
import cls from 'classnames'
import { useIntl } from 'react-intl'
import { useAudioPlayer, useAudioPosition } from 'react-use-audio-player'

import { useIsMobile } from 'hooks/useIsMobile'
import { actionTypes } from 'state/actionTypes'
import { AppContext } from 'state/appContext'

import { formatTime } from './progress'
import { ArticleAudioPlayerState } from './types'

type Props = {
  articleId: number
  children?: ReactNode
  onClickPlay: () => void
  audioPlayerState: ArticleAudioPlayerState
}

const AudioSidebar = ({
  articleId,
  audioPlayerState,
  onClickPlay,
  children,
}: Props) => {
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()
  const { play, pause } = useAudioPlayer()
  const { dispatch } = useContext(AppContext)
  const { position, duration } = useAudioPosition()
  const totalTime = useMemo(() => formatTime(duration), [duration])
  const labelOfTime = useMemo(() => {
    return formatTime(position)
  }, [position, totalTime])

  const stylesTooltip: TooltipProps['styles'] = useMemo(
    () => ({
      tooltip: {
        fontSize: 12,
        padding: '8px',
        color: theme.colors.gray600,
        '::before': {
          content: '""',
          position: 'absolute',
          width: '0.5rem',
          height: '0.5rem',
          backgroundColor: theme.colors.white,
          left: '-3px',
          top: '50%',
          transform: 'translateY(-50%) rotate(45deg)',
          zIndex: 0,
        },
      },
      arrow: {
        transform: 'none !important',
        top: '0 !important',
        left: '0 !important',
        height: '100% !important',
        width: '100% !important',
        borderRadius: '4px !important',
        zIndex: -1,
        backgroundColor: theme.colors.white,
        boxShadow: '1px 0px 11px 0px rgba(0, 0, 0, 0.1)',
      },
    }),
    [theme],
  )
  const handleClickPlay = () => {
    onClickPlay()
    play()
    dispatch({ type: actionTypes.PLAY_ARTICLE, payload: { id: articleId } })
  }

  const handleClickPause = () => {
    pause()
  }

  const handleClickResume = () => {
    handleClickPlay()
  }

  const isPlaying = audioPlayerState === 'playing'
  const isPaused = audioPlayerState === 'paused'
  const rendered = isPlaying
    ? {
        icon: <Pause />,
        onClick: handleClickPause,
      }
    : isPaused
    ? {
        icon: <Play />,
        onClick: handleClickResume,
      }
    : {
        icon: <Headphone />,
        onClick: handleClickPlay,
      }

  const tracking = useMemo(() => {
    return {
      'data-event-category': isMobile ? 'Sticky Bottom' : 'Audio',
      'data-event-action': isMobile
        ? 'Audio - article'
        : 'Request Article Audio',
      'data-event-label': isPlaying ? 'Paused' : isPaused ? 'Play' : 'Start',
    }
  }, [isPlaying, isPaused, isMobile])

  const renderedIcon = (
    <Box
      onClick={rendered.onClick}
      className={cls(
        'w-10 h-10 p-2 relative cursor-pointer pointer-event-child-none',
        {
          'rounded-lg hover:bg-gray100': !isMobile,
          'bg-gray100': isPlaying && !isMobile,
        },
      )}
      {...tracking}
    >
      {rendered.icon}
      {children}
    </Box>
  )
  const isOpenedLabel = ['playing', 'paused'].includes(audioPlayerState)
    ? true
    : undefined

  if (isMobile)
    return (
      <Box className="flex items-center">
        {renderedIcon}
        {isOpenedLabel && (
          <Text size="n1" color={theme.colors.gray800}>
            {labelOfTime}
          </Text>
        )}
      </Box>
    )

  return (
    <Tooltip
      opened={isOpenedLabel}
      position="right"
      withArrow
      styles={stylesTooltip}
      label={
        <Text size="n1" color={theme.colors.gray800}>
          {isPlaying || isPaused
            ? labelOfTime
            : f({
                id: 'Listen to this article',
                defaultMessage: 'Listen to this article',
              })}
        </Text>
      }
    >
      {renderedIcon}
    </Tooltip>
  )
}

export default AudioSidebar
